import { useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { useMutation } from "urql"
import { useShallow } from "zustand/react/shallow"

import type { ProductSelectionItem, PurchaseOrderFormData } from "../Supplier/types/types"
import { getPOLocationAssociationId } from "../utils/locationUtils"

import type { Order, OrderMutationUpdateArgs } from "@/graphql/codegen/graphql"
import { CreatePurchaseOrderDocument, UpdatePurchaseOrderDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { createPOFormData } from "@/screens/Procurement/PurchaseOrder/hooks/utils/common"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UsePurchaseOrderUpsertReturn {
  createdPO: Order | undefined
  handleCreatePO: (data: PurchaseOrderFormData, selectedProducts: ProductSelectionItem[]) => void
  updatePO: (data: OrderMutationUpdateArgs) => void
  fetching: boolean
  fetchingUpdate: boolean
}

export const usePurchaseOrderUpsert = (): UsePurchaseOrderUpsertReturn => {
  const navigate = useNavigate()
  const notify = useNotificationsStore(useShallow((s) => s.enqueueNotification))
  const [{ data: createdPOResult, fetching: creatingPO }, createPO] = useMutation(CreatePurchaseOrderDocument)
  const [{ fetching: updatingPO }, updatePO] = useMutation(UpdatePurchaseOrderDocument)

  const createdPO = useMemo(() => {
    if (createdPOResult?.order.create.__typename !== "OrderCreateSuccess") return undefined
    return createdPOResult.order.create.order as Order
  }, [createdPOResult])

  useEffect(() => {
    if (createdPOResult?.order.create.__typename === "OrderCreateSuccess") {
      notify({ message: "Purchase order created", type: NotificationType.Success })
      navigate({
        to: "/app/procurement/purchase-orders/$purchaseOrderId",
        params: { purchaseOrderId: createdPO!.orderId.toString() },
      })
    }
    if (createdPOResult?.order.create.__typename === "OrderCreateFailure") {
      console.error(createdPOResult.order.create.error)
      notify({ message: "Couldn't create PO, please make sure products are valid", type: NotificationType.Error })
    }
  }, [createdPO, createdPOResult, navigate, notify])

  const handleCreatePO = (data: PurchaseOrderFormData, selectedProducts: ProductSelectionItem[]) => {
    const sourceLocationId = getPOLocationAssociationId(selectedProducts)

    const draftPO = createPOFormData(data, selectedProducts)

    if (!draftPO?.success) {
      console.error(draftPO?.error.issues)
      let message = "Couldn't create PO, please check the form data"
      if (!sourceLocationId) {
        message = "Couldn't create PO. Please make sure product or company have associated locations"
      }
      notify({ message, type: NotificationType.Error })
      return
    }
    createPO({ input: draftPO.data })
  }

  return {
    createdPO,
    fetching: creatingPO,
    fetchingUpdate: updatingPO,
    handleCreatePO,
    updatePO,
  }
}
